import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { FormikConfig, FormikProps } from 'formik';
import { Form, Formik } from 'formik';
import { TranslationKeys } from 'app/translations';
import type { Connection } from 'app/types';
import { useSnakeBar } from 'app/hooks';
import { useConnectionUploadInsurerDataMutation } from '../../../../../hooks/api/baseline-measurements/useComparisonUploadInsurerDataMutation';
import {
  COMPARISON_INSURER_DATA_FILE_HEADERS,
  COMPARISON_INSURER_DATA_FILE_STRUCTURE,
  COMPARISON_INSURER_DATA_TEMPLATE_FILE_NAME,
  type Comparison,
  type InsurerEmployeeData,
  type PayloadData,
} from '../../../../../types/BaselineMeasurements';
import * as XLSX from 'xlsx';
import { FileType, HasAccessTo } from 'app/components';
import { ListFile } from 'app/components/FileUploadAreaWithTemplate/ListFile';
import { CONNECTION } from '../../../../../common/Authorization/entities';
import { RUN, VIEW } from '../../../../../common/Authorization/permissions';
import { DivStyled } from '../../../../Connections/Pages/CompareBatchPage/components/CompareBatchPageContent/components/CompareBatchFileUpload/CompareBatchFileUpload.styles';

import { ComparisonInsurerDataFileReadStrategy } from '../../../Services/ComparisonInsurerDataFileReadStrategy';
import { convertToISOString, formatDateDayMonthYear } from '../../../../../utils/converters/dateConverter';
import FileUploadAreaWithTemplateHorizontal from '../../../../../components/FileUploadAreaWithTemplate/FileUploadAreaWithTemplateHorizontal';

export const COMPARISON_INSURER_DATA_FILE_UPLOAD_DATA_TEST_ID = 'comparison_insurer_data_file_upload_component';

type FormValues = {
  uploadError: boolean;
  file?: File;
};

type ParsedEmployeeData = {
  'employee.name_first': string;
  'employee.name_prefix': string;
  'employee.name_last': string;
  'employee.date_birth': string;
  'employee.gender': string;
  'employee.email': string;
  'employee.bsn': string;
  'employee.civil_status': string;
  'employee.number': string;
  'employment.start': string;
  'employment.end': string;
  'employment.wage': string | number;
  'employment.parttime': string | number;
  'employee.start_date_full_time_annual_wage': string;
  'employee.start_date_part_time_percentage': string;
};

type ComparisonInsurerDataFileUploadProps = {
  comparisonId: Comparison['comparisonId'] | undefined;
  connectionId: Connection['connectionId'] | undefined;
  isLoading?: boolean;
  onUploadSuccess?: () => void;
};

export const ComparisonInsurerDataFileUpload = ({
  comparisonId,
  connectionId,
  isLoading = false,
  onUploadSuccess,
}: ComparisonInsurerDataFileUploadProps) => {
  const intl = useIntl();
  const { showErrorSnakeBar } = useSnakeBar();
  const canDownloadTemplate = HasAccessTo(CONNECTION, VIEW);
  const canUploadFile = HasAccessTo(CONNECTION, RUN);
  const { mutate: uploadInsurerDataMutation, isLoading: isUploading } = useConnectionUploadInsurerDataMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parseCSVFile = (csvContent: string): string => {
    const csvRows = csvContent.split('\n');
    const headers = csvRows[0].split(';');

    const birthdateIndex = headers.findIndex((header: string) => header.includes('Birthdate'));
    const startDateIndex = headers.findIndex((header: string) => header.includes('Employment Start Date'));
    const endDateIndex = headers.findIndex((header: string) => header.includes('Employment End Date'));
    const startDateFullTimeAnnualWageIndex = headers.findIndex((header: string) =>
      header.includes('Employee Start Date Full Time Annual Wage'),
    );
    const startDatePartTimePercentageIndex = headers.findIndex((header: string) =>
      header.includes('Employee Start Date Part Time Percentage'),
    );

    const payloadData: PayloadData[] = csvRows.slice(1).map((row: string) => {
      const values = row.split(';');
      const payload: Partial<PayloadData> = {};

      if (birthdateIndex >= 0) {
        payload['Birthdate (dd-mm-yyyy)'] = values[birthdateIndex]?.trim() || '';
      }
      if (startDateIndex >= 0) {
        payload['Employment Start Date (dd-mm-yyyy)'] = values[startDateIndex]?.trim() || '';
      }

      if (endDateIndex >= 0) {
        payload['Employment End Date (dd-mm-yyyy)'] = values[endDateIndex]?.trim() || '';
      }

      if (startDateFullTimeAnnualWageIndex >= 0) {
        payload['Employee Start Date Full Time Annual Wage (dd-mm-yyyy)'] =
          values[startDateFullTimeAnnualWageIndex]?.trim() || '';
      }

      if (startDatePartTimePercentageIndex >= 0) {
        payload['Employee Start Date Part Time Percentage (dd-mm-yyyy)'] =
          values[startDatePartTimePercentageIndex]?.trim() || '';
      }
      return payload as PayloadData;
    });

    const modifiedPayloadData = payloadData.map(employee => ({
      'Birthdate (dd-mm-yyyy)': convertToISOString(employee['Birthdate (dd-mm-yyyy)']),
      'Employment Start Date (dd-mm-yyyy)': convertToISOString(employee['Employment Start Date (dd-mm-yyyy)']),
      'Employment End Date (dd-mm-yyyy)': convertToISOString(employee['Employment End Date (dd-mm-yyyy)']),
      'Employee Start Date Full Time Annual Wage (dd-mm-yyyy)': convertToISOString(
        employee['Employee Start Date Full Time Annual Wage (dd-mm-yyyy)'],
      ),
      'Employee Start Date Part Time Percentage (dd-mm-yyyy)': convertToISOString(
        employee['Employee Start Date Part Time Percentage (dd-mm-yyyy)'],
      ),
    }));

    const modifiedCsvRows = csvRows.slice(1).map((row: string, index: number) => {
      const values = row.split(';');

      if (birthdateIndex >= 0) {
        values[birthdateIndex] = modifiedPayloadData[index]['Birthdate (dd-mm-yyyy)'];
      }
      if (startDateIndex >= 0) {
        values[startDateIndex] = modifiedPayloadData[index]['Employment Start Date (dd-mm-yyyy)'];
      }
      if (endDateIndex >= 0) {
        values[endDateIndex] = modifiedPayloadData[index]['Employment End Date (dd-mm-yyyy)'];
      }
      if (startDateFullTimeAnnualWageIndex >= 0) {
        values[startDateFullTimeAnnualWageIndex] =
          modifiedPayloadData[index]['Employee Start Date Full Time Annual Wage (dd-mm-yyyy)'];
      }
      if (startDatePartTimePercentageIndex >= 0) {
        values[startDatePartTimePercentageIndex] =
          modifiedPayloadData[index]['Employee Start Date Part Time Percentage (dd-mm-yyyy)'];
      }

      return values.join(';');
    });

    return [csvRows[0], ...modifiedCsvRows].join('\n');
  };

  const parseExcelFile = useCallback(
    (file: File): Promise<ParsedEmployeeData[]> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
          try {
            const payload = event.target?.result;
            if (typeof payload !== 'string') {
              throw new Error('Invalid file content');
            }
            const parsedPayload = parseCSVFile(payload);
            const workbook = XLSX.read(parsedPayload, { type: 'binary', dateNF: 'dd-mm-yyyy' });
            const sheet = workbook.Sheets[Object.keys(workbook.Sheets)[0]];
            const range = XLSX.utils.decode_range(sheet['!ref'] || '');

            range.s.r = 1;
            const employeeData = XLSX.utils.sheet_to_json<ParsedEmployeeData>(sheet, {
              header: [
                'employee.name_first',
                'employee.name_prefix',
                'employee.name_last',
                'employee.date_birth',
                'employee.gender',
                'employee.email',
                'employee.bsn',
                'employee.civil_status',
                'employee.number',
                'employment.start',
                'employment.end',
                'employment.wage',
                'employment.parttime',
                'employee.start_date_full_time_annual_wage',
                'employee.start_date_part_time_percentage',
              ],
              blankrows: false,
              skipHidden: true,
              rawNumbers: true,
              raw: false,
              range: range,
            });

            resolve(employeeData);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error parsing file:', error);
            showErrorSnakeBar({
              message: intl.formatMessage({ id: TranslationKeys.global_uploadFile_error }),
            });
            reject(error);
          }
        };
        reader.onerror = () => {
          // eslint-disable-next-line no-console
          console.error('File read error:', file.name);
          reject(new Error(`Error occurred reading file: ${file.name}`));
        };
        reader.readAsBinaryString(file);
      });
    },
    [intl, parseCSVFile, showErrorSnakeBar],
  );

  const formikInitialValues = React.useMemo<FormValues>(() => {
    return {
      uploadError: false,
      file: undefined,
    };
  }, []);

  const onFilesChange = React.useCallback(
    (form: FormikProps<FormValues>) => (files: File[]) => {
      form.setFieldValue('file', files[0]);
    },
    [],
  );

  const onChangeList = React.useCallback(
    (form: FormikProps<FormValues>) => () => {
      form.submitForm();
    },
    [],
  );

  const initialValues = React.useMemo<Array<{ element: string; value: { fieldName: string } }>>(() => {
    return COMPARISON_INSURER_DATA_FILE_STRUCTURE.map(fileStructure => ({
      element: fileStructure.headerTitle,
      value: {
        fieldName: fileStructure.fieldName,
      },
    }));
  }, []);

  const formikOnSubmit = React.useCallback<FormikConfig<FormValues>['onSubmit']>(
    async ({ uploadError, file }) => {
      if (file) {
        await parseExcelFile(file)
          .then(async data => {
            if (!uploadError && connectionId && comparisonId && data) {
              const method = 'uploadInsurerData';
              const payload: InsurerEmployeeData[] = data.map((employee: ParsedEmployeeData) => ({
                socialSecurityNumber: employee['employee.bsn'],
                employmentStartDate: formatDateDayMonthYear(employee['employment.start']),
                employmentEndDate: formatDateDayMonthYear(employee['employment.end']),
                annualWage:
                  typeof employee['employment.wage'] === 'number'
                    ? employee['employment.wage']
                    : Number.parseFloat(employee['employment.wage'] ?? '0'),
                partTimePercentage:
                  typeof employee['employment.parttime'] === 'number'
                    ? employee['employment.parttime']
                    : Number.parseFloat(employee['employment.parttime'] ?? '0'),
                firstName: employee['employee.name_first'] || null,
                middleName: employee['employee.name_prefix'] || null,
                lastName: employee['employee.name_last'] || null,
                dateOfBirth: formatDateDayMonthYear(employee['employee.date_birth']),
                gender: employee['employee.gender'] || null,
                emailAddress: employee['employee.email'] || null,
                civilStatus: employee['employee.civil_status'] || null,
                personnelNumber: employee['employee.number'] || null,
                startDateFullTimeAnnualWage: formatDateDayMonthYear(
                  employee['employee.start_date_full_time_annual_wage'],
                ),
                startDatePartTimePercentage: formatDateDayMonthYear(
                  employee['employee.start_date_part_time_percentage'],
                ),
              }));

              uploadInsurerDataMutation(
                {
                  comparisonId: comparisonId,
                  connectionId: connectionId,
                  employeeData: payload,
                },
                {
                  onSuccess: onUploadSuccess,
                  onError: error => {
                    showErrorSnakeBar({
                      method,
                      message: intl.formatMessage({ id: TranslationKeys.global_uploadFile_error }),
                    });
                    // eslint-disable-next-line no-console
                    console.error(error);
                  },
                },
              );
            }
          })
          .catch(() => {})
          .finally(() => {});
      }
    },
    [parseExcelFile, comparisonId, connectionId, uploadInsurerDataMutation, showErrorSnakeBar, intl, onUploadSuccess],
  );
  const readFileStrategy = new ComparisonInsurerDataFileReadStrategy(intl);
  const listFile = new ListFile(COMPARISON_INSURER_DATA_FILE_HEADERS, readFileStrategy);

  return (
    <Formik<FormValues> initialValues={formikInitialValues} onSubmit={formikOnSubmit}>
      {form => (
        <Form noValidate>
          <DivStyled data-testid={COMPARISON_INSURER_DATA_FILE_UPLOAD_DATA_TEST_ID}>
            <FileUploadAreaWithTemplateHorizontal
              title={TranslationKeys.global_download_templateFile}
              fileName={COMPARISON_INSURER_DATA_TEMPLATE_FILE_NAME}
              allowedFileTypes={[FileType.Csv]}
              initialFiles={[]}
              onFilesChange={onFilesChange(form)}
              onChangeList={onChangeList(form)}
              showDropArea={true}
              initialValues={initialValues}
              listFile={listFile}
              loading={isLoading || isUploading}
              disabledDownload={!canDownloadTemplate}
              disabledUpload={!canUploadFile}
            />
          </DivStyled>
        </Form>
      )}
    </Formik>
  );
};
